import React, {useEffect, useState} from 'react';
import {
  ApiRoleInputDto,
  ApiProblemDetails,
  ApiPermissionGroupBasicOutputDto,
  ApiPermissionBasicOutputDto,
  ApiRoleOutputDto,
} from '../../../types/api';
import {
  ContextPanel,
  ContextPanelContentSection,
  FormikValmetTextInput,
  FormCancelConfirmDialog,
  FormikValmetComplexMultiSelect,
  errorNotification,
  LoadingScreen,
} from '@valmet-iop/ui-common';
import {Formik} from 'formik';
import {apiGetRequest, apiPostRequest} from '../../../services/api/apiService';
import * as Yup from 'yup';
import {useTranslation} from 'react-i18next';

const createRole = async (model: ApiRoleInputDto) => {
  const result = await apiPostRequest<ApiRoleInputDto, ApiRoleOutputDto>(
    'api/roles',
    model,
  );
  return result;
};

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('roles:create.errors.nameRequired')
    .max(128, 'roles:create.errors.nameMaxLength'),
  description: Yup.string().max(
    256,
    'roles:create.errors.descriptionMaxLength',
  ),
});

const defaultValues: {
  name: string;
  description: string;
  selectedPermissionGroups: {text: string; value: string}[];
  selectedPermissions: {text: string; value: string}[];
} = {
  name: '',
  description: '',
  selectedPermissionGroups: [],
  selectedPermissions: [],
};

const emptyOptions: {
  text: string;
  value: string;
}[] = [];

const CreateSidebar = (props: {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (model: ApiRoleOutputDto) => void;
}) => {
  const {t} = useTranslation(['translation', 'roles']);
  const [isCancelConfirmDialogOpen, setCancelConfirmDialogOpen] = useState(
    false,
  );
  const [permissionGroupOptions, setPermissionGroupOptions] = useState(
    emptyOptions,
  );
  const [permissionOptions, setPermissionOptions] = useState(emptyOptions);
  const [isLoading, setIsLoading] = useState(false);

  const isOpen = props.isOpen;

  useEffect(() => {
    if (isOpen) {
      const getPermissionGroups = async () => {
        try {
          const result = await apiGetRequest<
            ApiPermissionGroupBasicOutputDto[]
          >('api/permissionGroups');
          setPermissionGroupOptions(
            result.map(dto => ({
              text: dto.groupName,
              value: dto.permissionGroupId,
            })),
          );
        } catch {
          errorNotification(
            t('roles:create.errors.lookupPermissionGroupsFailed'),
          );
        }
      };

      const getPermissions = async () => {
        try {
          const result = await apiGetRequest<ApiPermissionBasicOutputDto[]>(
            'api/permissions',
          );
          setPermissionOptions(
            result.map(dto => ({
              text: dto.displayName,
              value: dto.permissionId,
            })),
          );
        } catch {
          errorNotification(t('roles:create.errors.lookupPermissionsFailed'));
        }
      };

      setIsLoading(true);
      Promise.all([getPermissionGroups(), getPermissions()]).finally(() =>
        setIsLoading(false),
      );
    }
  }, [isOpen, t]);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={async (values, {setSubmitting, setFieldError, resetForm}) => {
        setSubmitting(true);
        try {
          const result = await createRole({
            name: values.name,
            description: values.description,
            permissions: values.selectedPermissions.map(g => g.value),
            permissionGroups: values.selectedPermissionGroups.map(g => g.value),
          });
          props.onCreate(result);
          resetForm();
        } catch (e) {
          errorNotification(t('roles:create.errors.createFailed'));
          if ((e as ApiProblemDetails)?.status === 409) {
            setFieldError('name', 'roles:create.errors.conflict');
          }
        } finally {
          setSubmitting(false);
        }
      }}
      validationSchema={validationSchema}
    >
      {({isSubmitting, values, errors, dirty, handleSubmit, resetForm}) => {
        const onCloseClick = () => {
          if (dirty && !isSubmitting) {
            setCancelConfirmDialogOpen(true);
            return;
          }

          resetForm();
          props.onClose();
        };
        const onCancelConfirmDialogResult = (confirmed: boolean) => {
          setCancelConfirmDialogOpen(false);
          if (confirmed) {
            resetForm();
            props.onClose();
          }
        };
        return (
          <>
            <ContextPanel
              open={props.isOpen}
              size="small"
              titleTranslationKey="roles:create.title"
              bottomActions={[
                {
                  translationKey: 'roles:create.buttons.save',
                  style: 'primary',
                  isDisabled:
                    isSubmitting ||
                    Object.keys(errors).length > 0 ||
                    values === defaultValues,
                  onClick: handleSubmit,
                },
                {
                  translationKey: 'roles:create.buttons.cancel',
                  style: 'ghost',
                  isDisabled: isSubmitting,
                  onClick: onCloseClick,
                },
              ]}
              onCloseClick={onCloseClick}
            >
              {isSubmitting || isLoading ? (
                <LoadingScreen />
              ) : (
                <>
                  <ContextPanelContentSection headerTranslationKey="roles:create.general.header">
                    <FormikValmetTextInput
                      formProperty="name"
                      id="name"
                      labelTranslationKey="roles:create.general.name"
                      isRequired
                    />
                    <FormikValmetTextInput
                      formProperty="description"
                      id="description"
                      labelTranslationKey="roles:create.general.description"
                    />
                  </ContextPanelContentSection>
                  <ContextPanelContentSection headerTranslationKey="roles:create.permissionGroups.header">
                    <FormikValmetComplexMultiSelect
                      formProperty="selectedPermissionGroups"
                      options={permissionGroupOptions}
                      selectPlaceholderTranslationKey="roles:create.permissionGroups.addNewPlaceholder"
                    />
                  </ContextPanelContentSection>
                  <ContextPanelContentSection headerTranslationKey="roles:create.individualPermissions.header">
                    <FormikValmetComplexMultiSelect
                      formProperty="selectedPermissions"
                      options={permissionOptions}
                      selectPlaceholderTranslationKey="roles:create.individualPermissions.addNewPlaceholder"
                    />
                  </ContextPanelContentSection>
                </>
              )}
            </ContextPanel>
            <FormCancelConfirmDialog
              isOpen={isCancelConfirmDialogOpen}
              onResult={onCancelConfirmDialogResult}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default CreateSidebar;
